import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  FiShield,
  FiSearch,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
} from "react-icons/fi";
import { verifyHallmarkThunk } from "../../../store/thunks/adminProductThunks";
import Button from "../../../components/common/Button";

// Hallmark Management Section
export const HallmarkManagementSection = ({ formData, onChange, errors }) => {
  const dispatch = useDispatch();
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  const handleVerifyHUID = async () => {
    if (!formData.hallmark.huid) {
      alert("Please enter HUID to verify");
      return;
    }

    setVerifying(true);
    try {
      const result = await dispatch(
        verifyHallmarkThunk(formData.hallmark.huid)
      ).unwrap();
      setVerificationResult(result);
    } catch (error) {
      setVerificationResult({
        verified: false,
        message: error.message || "Verification failed",
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium">Hallmark Information</h3>
        <FiShield className="text-2xl text-yellow-600" />
      </div>

      <div className="mb-6">
        <label className="flex items-center">
          <input
            type="checkbox"
            name="hallmark.isHallmarked"
            checked={formData.hallmark.isHallmarked}
            onChange={onChange}
            className="rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="ml-2 font-medium">
            This product is BIS hallmarked
          </span>
        </label>
      </div>

      {formData.hallmark.isHallmarked && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HUID (Hallmark Unique ID) *
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="hallmark.huid"
                  value={formData.hallmark.huid}
                  onChange={onChange}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors["hallmark.huid"]
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter 6-digit HUID"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleVerifyHUID}
                  disabled={verifying || !formData.hallmark.huid}
                >
                  {verifying ? (
                    <div className="animate-spin">⟳</div>
                  ) : (
                    <FiSearch />
                  )}
                </Button>
              </div>
              {errors["hallmark.huid"] && (
                <p className="mt-1 text-sm text-red-500">
                  {errors["hallmark.huid"]}
                </p>
              )}

              {verificationResult && (
                <div
                  className={`mt-2 p-2 rounded-lg flex items-start gap-2 ${
                    verificationResult.verified
                      ? "bg-green-50 text-green-800"
                      : "bg-red-50 text-red-800"
                  }`}
                >
                  {verificationResult.verified ? (
                    <FiCheckCircle className="mt-0.5" />
                  ) : (
                    <FiAlertCircle className="mt-0.5" />
                  )}
                  <div className="text-sm">
                    <p className="font-medium">{verificationResult.message}</p>
                    {verificationResult.data && (
                      <p className="text-xs mt-1">
                        Product: {verificationResult.data.name}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assaying & Hallmarking Center Code *
              </label>
              <input
                type="text"
                name="hallmark.centerCode"
                value={formData.hallmark.centerCode}
                onChange={onChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors["hallmark.centerCode"]
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
                placeholder="Enter center code"
              />
              {errors["hallmark.centerCode"] && (
                <p className="mt-1 text-sm text-red-500">
                  {errors["hallmark.centerCode"]}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Jeweller's Identification Code
              </label>
              <input
                type="text"
                name="hallmark.jewellerCode"
                value={formData.hallmark.jewellerCode}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter jeweller code"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year of Marking
              </label>
              <input
                type="text"
                name="hallmark.year"
                value={formData.hallmark.year}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Year"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purity Grade
              </label>
              <input
                type="text"
                name="hallmark.purity"
                value={formData.hallmark.purity || formData.purity}
                onChange={onChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="e.g., 916, 750, 585"
              />
            </div>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 rounded-lg flex items-start gap-2">
            <FiInfo className="text-yellow-600 mt-1" />
            <div className="text-sm text-yellow-800">
              <p className="font-medium">BIS Hallmarking Guidelines</p>
              <ul className="mt-1 space-y-0.5 text-xs">
                <li>
                  • HUID is mandatory for all hallmarked jewelry from July 2021
                </li>
                <li>
                  • Each piece must have a unique 6-digit alphanumeric HUID
                </li>
                <li>• Verify HUID authenticity through BIS portal</li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Bulk Operations Component
export const BulkOperationsModal = ({
  isOpen,
  onClose,
  selectedProducts = [],
  onExecute,
}) => {
  const [operation, setOperation] = useState("");
  const [bulkData, setBulkData] = useState({
    status: "",
    branchCode: "",
    stockAdjustment: 0,
    priceAdjustment: 0,
    tags: [],
  });

  if (!isOpen) return null;

  const handleExecute = () => {
    if (!operation) {
      alert("Please select an operation");
      return;
    }

    const updates = selectedProducts.map((productId) => ({
      productId,
      updates: getBulkUpdateData(),
    }));

    onExecute(updates);
  };

  const getBulkUpdateData = () => {
    switch (operation) {
      case "status":
        return { status: bulkData.status };
      case "stock":
        return {
          stock: {
            adjustment: bulkData.stockAdjustment,
            branchCode: bulkData.branchCode,
          },
        };
      case "price":
        return {
          pricingConfig: {
            adjustment: bulkData.priceAdjustment,
          },
        };
      case "tags":
        return { tags: bulkData.tags };
      default:
        return {};
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Bulk Operations</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {selectedProducts.length} products selected for bulk operation
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Operation
            </label>
            <select
              value={operation}
              onChange={(e) => setOperation(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">-- Select Operation --</option>
              <option value="status">Update Status</option>
              <option value="stock">Adjust Stock</option>
              <option value="price">Adjust Pricing</option>
              <option value="tags">Update Tags</option>
              <option value="branch">Add to Branch</option>
            </select>
          </div>

          {operation === "status" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Status
              </label>
              <select
                value={bulkData.status}
                onChange={(e) =>
                  setBulkData({ ...bulkData, status: e.target.value })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">Select Status</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="DRAFT">Draft</option>
                <option value="ARCHIVED">Archived</option>
              </select>
            </div>
          )}

          {operation === "stock" && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Branch Code
                </label>
                <input
                  type="text"
                  value={bulkData.branchCode}
                  onChange={(e) =>
                    setBulkData({ ...bulkData, branchCode: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter branch code"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stock Adjustment (+/-)
                </label>
                <input
                  type="number"
                  value={bulkData.stockAdjustment}
                  onChange={(e) =>
                    setBulkData({
                      ...bulkData,
                      stockAdjustment: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter adjustment value"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Positive values increase stock, negative values decrease
                </p>
              </div>
            </div>
          )}

          {operation === "price" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Adjustment (%)
              </label>
              <input
                type="number"
                value={bulkData.priceAdjustment}
                onChange={(e) =>
                  setBulkData({
                    ...bulkData,
                    priceAdjustment: parseFloat(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Enter percentage adjustment"
                step="0.1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Affects making charges percentage
              </p>
            </div>
          )}

          {operation === "tags" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Tags (comma separated)
              </label>
              <input
                type="text"
                value={bulkData.tags.join(", ")}
                onChange={(e) =>
                  setBulkData({
                    ...bulkData,
                    tags: e.target.value
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean),
                  })
                }
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="tag1, tag2, tag3"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleExecute}
            disabled={!operation || selectedProducts.length === 0}
          >
            Execute Bulk Operation
          </Button>
        </div>
      </div>
    </div>
  );
};

// Import/Export Component
export const ImportExportSection = ({ onImport, onExport }) => {
  const [importFile, setImportFile] = useState(null);
  const [exportFormat, setExportFormat] = useState("csv");

  const handleImport = () => {
    if (!importFile) {
      alert("Please select a file to import");
      return;
    }
    onImport(importFile);
    setImportFile(null);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium mb-4">Import/Export Products</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Import Products
          </h4>
          <div className="space-y-3">
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-full file:border-0
                file:text-sm file:font-semibold
                file:bg-primary file:text-white
                hover:file:bg-primary-dark"
            />
            <Button
              onClick={handleImport}
              disabled={!importFile}
              variant="outline"
              className="w-full"
            >
              Import Products
            </Button>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Export Products
          </h4>
          <div className="space-y-3">
            <select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="csv">CSV Format</option>
              <option value="excel">Excel Format</option>
              <option value="json">JSON Format</option>
            </select>
            <Button
              onClick={() => onExport(exportFormat)}
              variant="outline"
              className="w-full"
            >
              Export Products
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-xs text-blue-800">
          <strong>Import Format:</strong> CSV/Excel with columns: name, type,
          subType, grossWeight, netWeight, purity, karigarCode, stock,
          branchCode
        </p>
      </div>
    </div>
  );
};
